import {
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  H2,
  InputGroup,
  InputRightAddon,
  Spinner,
  TextInput,
} from '@ouster/webviz-react-components';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import useChakraHook from '../../hooks/useChakraHook';
import { useStoreState } from '../../reducers';
import {
  fetchUploadConfiguration,
  saveUploadConfiguration,
  UPLOAD_CONFIGURATION_QUERY_KEY,
  type UploadConfiguration,
} from './uploadConfiguration';

type FormState = {
  disruptionGracePeriodSeconds: number;
  activeRetryWindowMinutes: number;
  resumableUploadExpiryMinutes: number;
  resumableCancellationBufferMinutes: number;
};

const toFormState = (configuration: UploadConfiguration): FormState => ({
  disruptionGracePeriodSeconds: configuration.disruption_grace_period_seconds,
  activeRetryWindowMinutes: configuration.active_retry_window_seconds / 60,
  resumableUploadExpiryMinutes: configuration.resumable_upload_expiry_minutes,
  resumableCancellationBufferMinutes:
    configuration.resumable_cancellation_buffer_minutes,
});

export default function DevToolConfiguration() {
  const { showToast } = useChakraHook();
  const queryClient = useQueryClient();
  const curUserOrgId = useStoreState(
    (storeState) => storeState.user.curUserOrg?.id
  );
  const {
    data: configuration,
    isPending,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: [...UPLOAD_CONFIGURATION_QUERY_KEY, curUserOrgId],
    queryFn: () => fetchUploadConfiguration(curUserOrgId!),
    enabled: Boolean(curUserOrgId),
  });
  const [draft, setDraft] = useState<FormState | null>(null);
  const uploadConfigurationQueryKey = [
    ...UPLOAD_CONFIGURATION_QUERY_KEY,
    curUserOrgId,
  ];

  const baseline = configuration ? toFormState(configuration) : null;
  const form = draft ?? baseline;

  useEffect(() => {
    if (error) {
      showToast({
        title: 'Failed to load upload configuration',
        description: `${error}`,
        status: 'error',
      });
    }
  }, [error, showToast]);

  const saveMutation = useMutation({
    mutationFn: saveUploadConfiguration,
    onSuccess: (saved) => {
      queryClient.setQueryData(uploadConfigurationQueryKey, saved);
      setDraft(null);
      showToast({
        title: 'Upload configuration saved',
        description: 'The updated upload recovery settings are now active.',
        status: 'success',
      });
    },
    onError: (saveError: unknown) => {
      showToast({
        title: 'Failed to save upload configuration',
        description: `${saveError}`,
        status: 'error',
      });
    },
  });

  const updateDraft = (patch: Partial<FormState>) => {
    if (!baseline) {
      return;
    }
    setDraft((current) => ({ ...(current ?? baseline), ...patch }));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form) {
      return;
    }
    saveMutation.mutate({
      disruption_grace_period_seconds: form.disruptionGracePeriodSeconds,
      active_retry_window_seconds: form.activeRetryWindowMinutes * 60,
      resumable_upload_expiry_minutes: form.resumableUploadExpiryMinutes,
      resumable_cancellation_buffer_minutes:
        form.resumableCancellationBufferMinutes,
    });
  };

  return (
    <Box p="25px 36px 24px" mb={8} data-testid="configuration-page">
      <H2 fontWeight="medium" fontSize={30} mb={6}>
        Configuration
      </H2>

      {isPending && !configuration ? (
        <Flex
          alignItems="center"
          justifyContent="center"
          minH="12rem"
          data-testid="configuration-loading"
        >
          <Spinner size="xl" />
        </Flex>
      ) : null}

      {isError && !configuration ? (
        <Box color="red.300" fontSize={16} data-testid="configuration-error">
          Could not load the upload configuration. Refresh the page to try
          again.
        </Box>
      ) : null}

      {form ? (
        <Box
          display="grid"
          gridTemplateColumns={{ base: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }}
          rowGap="3rem"
          columnGap="3rem"
          alignItems="start"
          data-testid="configuration-sections"
        >
          <Box
            as="form"
            onSubmit={handleSubmit}
            width="full"
            border="1px solid"
            borderColor="gray.700"
            borderRadius="lg"
            backgroundColor="gray.900"
            overflow="hidden"
            data-testid="configuration-form"
          >
            <Box
              px={{ base: 5, md: 8 }}
              py={6}
              borderBottom="1px solid"
              borderColor="gray.700"
            >
              <H2
                fontWeight="semibold"
                fontSize={16}
                mb={2}
                textTransform="uppercase"
              >
                Upload Recovery &amp; Expiration
              </H2>
              <Box color="gray.300" fontSize={14} lineHeight={1.6}>
                Configure how the system handles temporary network drops and
                offline fallback states during large file uploads.
              </Box>
            </Box>

            <Box px={{ base: 5, md: 7 }}>
              <FormControl
                paddingInline={0}
                py={6}
                display="grid"
                gridTemplateColumns={{
                  base: '1fr',
                  md: 'minmax(0, 1fr) 14rem',
                }}
                alignItems="center"
                columnGap={10}
                rowGap={4}
                borderBottom="1px solid"
                borderColor="gray.700"
              >
                <Box minW={0}>
                  <FormLabel
                    htmlFor="disruption-grace-period"
                    color="gray.100"
                    fontWeight="semibold"
                    fontSize={14}
                    mb={2}
                  >
                    Disruption Grace Period
                  </FormLabel>
                  <FormHelperText
                    m={0}
                    minH="auto"
                    color="gray.400"
                    fontSize={14}
                    lineHeight={1.6}
                  >
                    The delay before displaying a warning toast during temporary
                    network drops. Helps prevent alert fatigue on unstable
                    connections.
                  </FormHelperText>
                </Box>
                <InputGroup size="md" maxW={{ base: 'full', md: '14rem' }}>
                  <TextInput
                    id="disruption-grace-period"
                    type="number"
                    min={1}
                    step={1}
                    value={form.disruptionGracePeriodSeconds}
                    onChange={(event) =>
                      updateDraft({
                        disruptionGracePeriodSeconds: Number(
                          event.target.value
                        ),
                      })
                    }
                    borderRightRadius={0}
                    data-testid="disruption-grace-period-input"
                  />
                  <InputRightAddon
                    height="2.5rem"
                    backgroundColor="background-surface"
                    borderLeft={0}
                    borderRightRadius="action"
                    color="gray.400"
                    fontSize={12}
                    textTransform="uppercase"
                  >
                    seconds
                  </InputRightAddon>
                </InputGroup>
              </FormControl>

              <FormControl
                paddingInline={0}
                py={6}
                display="grid"
                gridTemplateColumns={{
                  base: '1fr',
                  md: 'minmax(0, 1fr) 14rem',
                }}
                alignItems="center"
                columnGap={10}
                rowGap={4}
                borderBottom="1px solid"
                borderColor="gray.700"
              >
                <Box minW={0}>
                  <FormLabel
                    htmlFor="active-retry-window"
                    color="gray.100"
                    fontWeight="semibold"
                    fontSize={14}
                    mb={2}
                  >
                    Active Retry Window
                  </FormLabel>
                  <FormHelperText
                    m={0}
                    minH="auto"
                    color="gray.400"
                    fontSize={14}
                    lineHeight={1.6}
                  >
                    The time limit for automatic recovery attempts before safely
                    shifting the stalled upload to long-term offline mode.
                  </FormHelperText>
                </Box>
                <InputGroup size="md" maxW={{ base: 'full', md: '14rem' }}>
                  <TextInput
                    id="active-retry-window"
                    type="number"
                    min={1}
                    step={1}
                    value={form.activeRetryWindowMinutes}
                    onChange={(event) =>
                      updateDraft({
                        activeRetryWindowMinutes: Number(event.target.value),
                      })
                    }
                    borderRightRadius={0}
                    data-testid="active-retry-window-input"
                  />
                  <InputRightAddon
                    height="2.5rem"
                    backgroundColor="background-surface"
                    borderLeft={0}
                    borderRightRadius="action"
                    color="gray.400"
                    fontSize={12}
                    textTransform="uppercase"
                  >
                    minutes
                  </InputRightAddon>
                </InputGroup>
              </FormControl>

              <FormControl
                paddingInline={0}
                py={6}
                display="grid"
                gridTemplateColumns={{
                  base: '1fr',
                  md: 'minmax(0, 1fr) 14rem',
                }}
                alignItems="center"
                columnGap={10}
                rowGap={4}
                borderBottom="1px solid"
                borderColor="gray.700"
              >
                <Box minW={0}>
                  <FormLabel
                    htmlFor="resumable-upload-expiry"
                    color="gray.100"
                    fontWeight="semibold"
                    fontSize={14}
                    mb={2}
                  >
                    Resumable Upload Expiry
                  </FormLabel>
                  <FormHelperText
                    m={0}
                    minH="auto"
                    color="gray.400"
                    fontSize={14}
                    lineHeight={1.6}
                  >
                    How long a resumable upload URL stays valid after it is
                    issued. After this window the session can no longer be
                    resumed and must be restarted.
                  </FormHelperText>
                </Box>
                <InputGroup size="md" maxW={{ base: 'full', md: '14rem' }}>
                  <TextInput
                    id="resumable-upload-expiry"
                    type="number"
                    min={1}
                    step={1}
                    value={form.resumableUploadExpiryMinutes}
                    onChange={(event) =>
                      updateDraft({
                        resumableUploadExpiryMinutes: Number(
                          event.target.value
                        ),
                      })
                    }
                    borderRightRadius={0}
                    data-testid="resumable-upload-expiry-input"
                  />
                  <InputRightAddon
                    height="2.5rem"
                    backgroundColor="background-surface"
                    borderLeft={0}
                    borderRightRadius="action"
                    color="gray.400"
                    fontSize={12}
                    textTransform="uppercase"
                  >
                    minutes
                  </InputRightAddon>
                </InputGroup>
              </FormControl>

              <FormControl
                paddingInline={0}
                py={6}
                display="grid"
                gridTemplateColumns={{
                  base: '1fr',
                  md: 'minmax(0, 1fr) 14rem',
                }}
                alignItems="center"
                columnGap={10}
                rowGap={4}
              >
                <Box minW={0}>
                  <FormLabel
                    htmlFor="resumable-cancellation-buffer"
                    color="gray.100"
                    fontWeight="semibold"
                    fontSize={14}
                    mb={2}
                  >
                    Cancellation Buffer
                  </FormLabel>
                  <FormHelperText
                    m={0}
                    minH="auto"
                    color="gray.400"
                    fontSize={14}
                    lineHeight={1.6}
                  >
                    Grace period past expiry before the cleanup job cancels an
                    abandoned resumable session and removes its staging object.
                  </FormHelperText>
                </Box>
                <InputGroup size="md" maxW={{ base: 'full', md: '14rem' }}>
                  <TextInput
                    id="resumable-cancellation-buffer"
                    type="number"
                    min={1}
                    step={1}
                    value={form.resumableCancellationBufferMinutes}
                    onChange={(event) =>
                      updateDraft({
                        resumableCancellationBufferMinutes: Number(
                          event.target.value
                        ),
                      })
                    }
                    borderRightRadius={0}
                    data-testid="resumable-cancellation-buffer-input"
                  />
                  <InputRightAddon
                    height="2.5rem"
                    backgroundColor="background-surface"
                    borderLeft={0}
                    borderRightRadius="action"
                    color="gray.400"
                    fontSize={12}
                    textTransform="uppercase"
                  >
                    minutes
                  </InputRightAddon>
                </InputGroup>
              </FormControl>
            </Box>

            <Flex
              px={{ base: 5, md: 7 }}
              py={6}
              borderTop="1px solid"
              borderColor="gray.700"
              backgroundColor="blackAlpha.400"
              justifyContent="flex-end"
              gap={3}
            >
              <Button
                type="button"
                variant="outline"
                subtle
                minW="5.5rem"
                onClick={() => setDraft(null)}
                isDisabled={draft === null || saveMutation.isPending}
                data-testid="configuration-reset"
              >
                Reset
              </Button>
              <Button
                type="submit"
                variant="solid"
                minW="5.5rem"
                isDisabled={isFetching || saveMutation.isPending}
                isLoading={saveMutation.isPending}
                data-testid="configuration-save"
              >
                Save Changes
              </Button>
            </Flex>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
